'use client';

import clsx from 'clsx';
import { CarouselProvider, DotGroup, Dot } from 'pure-react-carousel';
import { Fragment } from 'react';

import styles from './Carousel.module.css';
import Slider from './Slider';
import { DefaultValue, CarouselProps, RenderDotsProps } from './types';
import BackButton from './Buttons/BackButton';
import NextButton from './Buttons/NextButton';

function Carousel<V = DefaultValue>(props: Readonly<CarouselProps<V>>) {
  const {
    slideData,
    className,
    hideButtons,
    hideDotButtons = false,
    disableDrag = false,
    buttonsAbsolute,
    dotClassName,
    arrowClassName,
    dragStep = 1,
    backArrowClasses,
    nextArrowClasses,
    carouselContentClassName,
    currentImageIndex,
    ...rest
  } = props;
  const dotClasses: string = clsx(styles.dots, styles.dotsButton, dotClassName);

  return (
    <CarouselProvider
      infinite
      lockOnWindowScroll
      step={rest.visibleSlides}
      className={clsx(styles.carousel, className)}
      naturalSlideWidth={100}
      naturalSlideHeight={100}
      totalSlides={slideData.length}
      visibleSlides={rest.visibleSlides}
      dragEnabled={!disableDrag}
      touchEnabled={!disableDrag}
      dragStep={dragStep}
      currentSlide={currentImageIndex}
    >
      <div className={clsx(styles.carouselContent, carouselContentClassName)}>
        <BackButton
          buttonsAbsolute={buttonsAbsolute}
          hideButtons={hideButtons}
          arrowClassName={arrowClassName}
          backArrowClasses={backArrowClasses}
        />

        <Slider slideData={slideData} {...rest} />

        <NextButton
          buttonsAbsolute={buttonsAbsolute}
          hideButtons={hideButtons}
          total={slideData.length}
          step={rest.visibleSlides}
          arrowClassName={arrowClassName}
          nextArrowClasses={nextArrowClasses}
        />
      </div>
      {!hideDotButtons && (
        <DotGroup
          className={dotClasses}
          showAsSelectedForCurrentSlideOnly={true}
          renderDots={(dotPros: RenderDotsProps) => {
            const totalDots = Math.ceil(dotPros.totalSlides! / dotPros.visibleSlides!);
            const renderDot = new Array<number>(totalDots).fill(0);
            for (let slideId = 1; slideId < renderDot.length; slideId++) {
              renderDot[slideId] = renderDot[slideId - 1] + dotPros.visibleSlides!;
            }
            return renderDot.map((slideIdx: number) => (
              <Fragment key={slideIdx}>
                <Dot slide={slideIdx} />
              </Fragment>
            ));
          }}
        />
      )}
    </CarouselProvider>
  );
}

export default Carousel;
